/**
 * File: ThreatModelingReport.tsx
 * Description: Renders threat modeling output in Shell's TaaC dashboard
 *   layout with service description cards, data flow visualization,
 *   STRIDE/LINDDUN/OWASP-LLM/CAPEC/SecLLM/LLM4Cyber threat tables,
 *   threat register, and security posture summary matching NATA format.
 * Author: System
 * Created: 2026-03-02
 * Last Modified: 2026-03-03
 */

import {
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  ServerStackIcon,
  GlobeAltIcon,
  CubeIcon,
  WrenchScrewdriverIcon,
  ArrowsRightLeftIcon,
  ClipboardDocumentListIcon,
  BoltIcon,
  CalendarDaysIcon,
  QuestionMarkCircleIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import {
  extractFencedBlocks,
  MERMAID_LANGUAGE,
  stripFencedBlocks,
} from '../../utils/markdownFences';
import { MermaidRenderer } from '../markdown/MermaidRenderer';

type ThreatSectionKey =
  | 'serviceDesc'
  | 'functionality'
  | 'dataProcessing'
  | 'network'
  | 'components'
  | 'pipeline'
  | 'systemInventory'
  | 'dataFlow'
  | 'existingControls'
  | 'componentThreats'
  | 'flowThreats'
  | 'threats'
  | 'gapAnalysis'
  | 'mitigations'
  | 'topActions'
  | 'assumptions'
  | 'securityPosture';

type ThreatSections = Record<ThreatSectionKey, string>;

const EMPTY_SECTIONS: ThreatSections = {
  serviceDesc: '',
  functionality: '',
  dataProcessing: '',
  network: '',
  components: '',
  pipeline: '',
  systemInventory: '',
  dataFlow: '',
  existingControls: '',
  componentThreats: '',
  flowThreats: '',
  threats: '',
  gapAnalysis: '',
  mitigations: '',
  topActions: '',
  assumptions: '',
  securityPosture: '',
};

const SECTION_ALIASES: Record<ThreatSectionKey, string[]> = {
  serviceDesc: ['service description'],
  functionality: ['service functionality', 'functionality'],
  dataProcessing: ['data processing details', 'data processing'],
  network: ['network information', 'network'],
  components: ['components'],
  pipeline: ['pipeline configuration', 'pipeline'],
  systemInventory: ['system inventory', 'inventory'],
  dataFlow: ['data flow'],
  existingControls: ['existing security controls', 'security controls'],
  componentThreats: ['component-level threats', 'component threats'],
  flowThreats: ['flow/boundary threats', 'flow boundary threats'],
  threats: ['threat analysis'],
  gapAnalysis: ['security gap analysis', 'gap analysis'],
  mitigations: [
    'actionable mitigations',
    'mitigations',
    'best practices for integrating threat modeling',
    'best practices for threat modeling',
    'best practices',
  ],
  topActions: ['top 30-60 day actions', 'top 30 60 day actions'],
  assumptions: ['assumptions & gaps', 'assumptions and gaps', 'assumptions'],
  securityPosture: [
    'security posture summary',
    'security posture',
    'summary',
  ],
};

interface ThreatModelingReportProps {
  /** Raw markdown content from the threat modeling agent */
  content: string;
}

/**
 * Card wrapper matching the NATA visual style with DaisyUI.
 */
const InfoCard: React.FC<{
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}> = ({ title, icon, children, className = '' }) => (
  <div
    className={`card bg-base-100 shadow-md border border-base-300 ${className}`}
  >
    <div className="card-body p-4">
      <h3 className="card-title text-sm font-semibold flex items-center gap-2">
        {icon}
        {title}
      </h3>
      <div className="text-sm mt-2 prose prose-sm max-w-none">
        {children}
      </div>
    </div>
  </div>
);

/**
 * Extract a section of markdown between two headings.
 *
 * @param content - Full markdown string
 * @param startHeading - Heading that starts the section
 * @param endHeadings - Headings that end the section
 * @returns Section content without its own heading
 */
function normalizeHeadingTitle(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/^\s*#+\s*/, '')
    .replace(/^\s*\d+[.)-]\s*/, '')
    .replace(/\*\*/g, '')
    .replace(/`/g, '')
    .replace(/[:-]+\s*$/, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function resolveSectionKey(heading: string): ThreatSectionKey | null {
  const normalized = normalizeHeadingTitle(heading);

  for (const key of Object.keys(SECTION_ALIASES) as ThreatSectionKey[]) {
    if (SECTION_ALIASES[key].some((alias) => normalized.includes(alias))) {
      return key;
    }
  }

  return null;
}

function parseThreatSections(content: string): ThreatSections {
  const sections: ThreatSections = { ...EMPTY_SECTIONS };
  const headingRegex = /^#{1,6}\s+(.+)$/gm;
  const matches: Array<{
    key: ThreatSectionKey;
    start: number;
    end: number;
  }> = [];

  let match: RegExpExecArray | null;
  while ((match = headingRegex.exec(content)) !== null) {
    const key = resolveSectionKey(match[1]);
    if (!key) {
      continue;
    }

    matches.push({
      key,
      start: match.index,
      end: match.index + match[0].length,
    });
  }

  for (let i = 0; i < matches.length; i += 1) {
    const current = matches[i];
    const next = matches[i + 1];
    const sectionContent = content
      .slice(current.end, next ? next.start : content.length)
      .trim();

    if (!sectionContent) {
      continue;
    }

    sections[current.key] = sections[current.key]
      ? `${sections[current.key]}\n\n${sectionContent}`
      : sectionContent;
  }

  return sections;
}

/**
 * Extract all mermaid code blocks from a markdown section.
 */
function extractMermaidBlocks(section: string): string[] {
  return extractFencedBlocks(section, MERMAID_LANGUAGE);
}

/**
 * Remove mermaid code blocks from markdown to render text separately.
 */
function removeMermaidBlocks(section: string): string {
  return stripFencedBlocks(section, MERMAID_LANGUAGE);
}

/**
 * ThreatModelingReport renders threat modeling content in the NATA
 * TaaC dashboard layout: info cards, system inventory, data flow,
 * component/flow-level threats, NATA threat table, mitigations,
 * threat register, assumptions & gaps, and security posture summary.
 */
export const ThreatModelingReport: React.FC<
  ThreatModelingReportProps
> = ({ content }) => {
  /** Parse each section */
  const sections = useMemo(() => parseThreatSections(content), [content]);

  /** Extract mermaid diagrams from data flow and posture */
  const dataFlowDiagrams = useMemo(
    () => extractMermaidBlocks(sections.dataFlow),
    [sections.dataFlow],
  );
  const postureDiagrams = useMemo(
    () => extractMermaidBlocks(sections.securityPosture),
    [sections.securityPosture],
  );

  const dataFlowText = useMemo(
    () => removeMermaidBlocks(sections.dataFlow),
    [sections.dataFlow],
  );
  const postureText = useMemo(
    () => removeMermaidBlocks(sections.securityPosture),
    [sections.securityPosture],
  );

  const hasParsedSections = useMemo(
    () => Object.values(sections).some((value) => value.trim().length > 0),
    [sections],
  );

  /** Markdown renderer with GFM table support */
  const renderMarkdown = (md: string) => (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        table: ({ children, ...props }) => (
          <table
            className="table table-zebra table-sm w-full"
            {...props}
          >
            {children}
          </table>
        ),
        th: ({ children, ...props }) => (
          <th
            className="bg-base-200 text-xs font-semibold"
            {...props}
          >
            {children}
          </th>
        ),
        td: ({ children, ...props }) => (
          <td className="text-xs" {...props}>
            {children}
          </td>
        ),
        strong: ({ children, ...props }) => (
          <strong className="font-semibold" {...props}>
            {children}
          </strong>
        ),
        h4: ({ children, ...props }) => (
          <h4
            className="text-sm font-bold mt-4 mb-2 text-warning"
            {...props}
          >
            {children}
          </h4>
        ),
      }}
    >
      {md}
    </ReactMarkdown>
  );

  if (!content || content.trim().length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-base-content/50">
        <ShieldCheckIcon className="h-8 w-8 mr-2" />
        <span>No threat modeling report generated yet.</span>
      </div>
    );
  }

  if (!hasParsedSections) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheckIcon className="h-6 w-6 text-primary" />
          <h2 className="text-lg font-bold">
            Threat Modeling Report
          </h2>
        </div>
        <div className="card bg-base-100 shadow-md border border-base-300">
          <div className="card-body p-4">
            <div className="prose prose-sm max-w-none overflow-x-auto">
              {renderMarkdown(content)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <ShieldCheckIcon className="h-6 w-6 text-primary" />
        <h2 className="text-lg font-bold">
          Threat Modeling Report
        </h2>
      </div>

      {/* Top info cards - NATA-style flex layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sections.serviceDesc && (
          <InfoCard
            title="Service Description"
            icon={
              <ServerStackIcon className="h-4 w-4 text-primary" />
            }
          >
            {renderMarkdown(sections.serviceDesc)}
          </InfoCard>
        )}

        {sections.functionality && (
          <InfoCard
            title="Service Functionality"
            icon={<CubeIcon className="h-4 w-4 text-secondary" />}
          >
            {renderMarkdown(sections.functionality)}
          </InfoCard>
        )}

        {sections.dataProcessing && (
          <InfoCard
            title="Data Processing Details"
            icon={
              <ShieldCheckIcon className="h-4 w-4 text-accent" />
            }
          >
            {renderMarkdown(sections.dataProcessing)}
          </InfoCard>
        )}

        {sections.network && (
          <InfoCard
            title="Network Information"
            icon={
              <GlobeAltIcon className="h-4 w-4 text-info" />
            }
          >
            {renderMarkdown(sections.network)}
          </InfoCard>
        )}

        {sections.components && (
          <InfoCard
            title="Components"
            icon={
              <CubeIcon className="h-4 w-4 text-success" />
            }
          >
            {renderMarkdown(sections.components)}
          </InfoCard>
        )}

        {sections.pipeline && (
          <InfoCard
            title="Pipeline Configuration"
            icon={
              <WrenchScrewdriverIcon className="h-4 w-4 text-warning" />
            }
          >
            {renderMarkdown(sections.pipeline)}
          </InfoCard>
        )}
      </div>

      {/* System Inventory */}
      {sections.systemInventory && (
        <div className="card bg-base-100 shadow-md border border-base-300">
          <div className="card-body p-4">
            <h3 className="card-title text-sm font-semibold flex items-center gap-2">
              <ClipboardDocumentListIcon className="h-4 w-4 text-secondary" />
              System Inventory
            </h3>
            <div className="mt-2 prose prose-sm max-w-none overflow-x-auto">
              {renderMarkdown(sections.systemInventory)}
            </div>
          </div>
        </div>
      )}

      {/* Data Flow Section */}
      {sections.dataFlow && (
        <div className="card bg-base-100 shadow-md border border-base-300">
          <div className="card-body p-4">
            <h3 className="card-title text-sm font-semibold flex items-center gap-2">
              <ArrowsRightLeftIcon className="h-4 w-4 text-primary" />
              Data Flow Information
            </h3>

            {dataFlowDiagrams.map((diagram, idx) => (
              <div
                key={`df-${idx}`}
                className="mt-4 bg-base-200 rounded-lg p-4"
              >
                <MermaidRenderer code={diagram} />
              </div>
            ))}

            {dataFlowText && (
              <div className="mt-4 prose prose-sm max-w-none">
                {renderMarkdown(dataFlowText)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Existing Security Controls (brownfield) */}
      {sections.existingControls && (
        <div className="card bg-base-100 shadow-md border border-base-300">
          <div className="card-body p-4">
            <h3 className="card-title text-sm font-semibold flex items-center gap-2">
              <ShieldCheckIcon className="h-4 w-4 text-success" />
              Existing Security Controls
            </h3>
            <div className="mt-2 prose prose-sm max-w-none">
              {renderMarkdown(sections.existingControls)}
            </div>
          </div>
        </div>
      )}

      {/* Component-Level Threats (STRIDE + LINDDUN) */}
      {sections.componentThreats && (
        <div className="card bg-base-100 shadow-md border border-base-300">
          <div className="card-body p-4">
            <h3 className="card-title text-sm font-semibold flex items-center gap-2">
              <ExclamationTriangleIcon className="h-4 w-4 text-warning" />
              Component-Level Threats (STRIDE + LINDDUN)
            </h3>
            <div className="mt-2 prose prose-sm max-w-none overflow-x-auto">
              {renderMarkdown(sections.componentThreats)}
            </div>
          </div>
        </div>
      )}

      {/* Flow/Boundary Threats */}
      {sections.flowThreats && (
        <div className="card bg-base-100 shadow-md border border-base-300">
          <div className="card-body p-4">
            <h3 className="card-title text-sm font-semibold flex items-center gap-2">
              <BoltIcon className="h-4 w-4 text-error" />
              Flow/Boundary Threats
            </h3>
            <div className="mt-2 prose prose-sm max-w-none overflow-x-auto">
              {renderMarkdown(sections.flowThreats)}
            </div>
          </div>
        </div>
      )}

      {/* Threat Analysis Table - core NATA layout */}
      {sections.threats && (
        <div className="card bg-base-100 shadow-md border border-base-300">
          <div className="card-body p-4">
            <h3 className="card-title text-sm font-semibold flex items-center gap-2">
              <ExclamationTriangleIcon className="h-4 w-4 text-error" />
              Threat Analysis
            </h3>
            <div className="mt-4 prose prose-sm max-w-none overflow-x-auto">
              {renderMarkdown(sections.threats)}
            </div>
          </div>
        </div>
      )}

      {/* Security Gap Analysis (brownfield) */}
      {sections.gapAnalysis && (
        <div className="card bg-base-100 shadow-md border border-base-300">
          <div className="card-body p-4">
            <h3 className="card-title text-sm font-semibold flex items-center gap-2">
              <ExclamationTriangleIcon className="h-4 w-4 text-warning" />
              Security Gap Analysis
            </h3>
            <div className="mt-2 prose prose-sm max-w-none overflow-x-auto">
              {renderMarkdown(sections.gapAnalysis)}
            </div>
          </div>
        </div>
      )}

      {/* Actionable Mitigations */}
      {sections.mitigations && (
        <div className="card bg-base-100 shadow-md border border-base-300">
          <div className="card-body p-4">
            <h3 className="card-title text-sm font-semibold flex items-center gap-2">
              <ShieldCheckIcon className="h-4 w-4 text-info" />
              Actionable Mitigations
            </h3>
            <div className="mt-2 prose prose-sm max-w-none overflow-x-auto">
              {renderMarkdown(sections.mitigations)}
            </div>
          </div>
        </div>
      )}

      {/* Top 30-60 Day Actions */}
      {sections.topActions && (
        <div className="card bg-base-100 shadow-md border border-base-300">
          <div className="card-body p-4">
            <h3 className="card-title text-sm font-semibold flex items-center gap-2">
              <CalendarDaysIcon className="h-4 w-4 text-accent" />
              Top 30-60 Day Actions
            </h3>
            <div className="mt-2 prose prose-sm max-w-none overflow-x-auto">
              {renderMarkdown(sections.topActions)}
            </div>
          </div>
        </div>
      )}

      {/* Assumptions & Gaps */}
      {sections.assumptions && (
        <div className="card bg-base-100 shadow-md border border-base-300">
          <div className="card-body p-4">
            <h3 className="card-title text-sm font-semibold flex items-center gap-2">
              <QuestionMarkCircleIcon className="h-4 w-4 text-warning" />
              Assumptions & Gaps
            </h3>
            <div className="mt-2 prose prose-sm max-w-none">
              {renderMarkdown(sections.assumptions)}
            </div>
          </div>
        </div>
      )}

      {/* Security Posture Summary with quadrant chart */}
      {sections.securityPosture && (
        <div className="card bg-base-100 shadow-md border border-base-300">
          <div className="card-body p-4">
            <h3 className="card-title text-sm font-semibold flex items-center gap-2">
              <ChartBarIcon className="h-4 w-4 text-error" />
              Security Posture Summary
            </h3>

            {postureText && (
              <div className="mt-2 prose prose-sm max-w-none">
                {renderMarkdown(postureText)}
              </div>
            )}

            {postureDiagrams.map((diagram, idx) => (
              <div
                key={`sp-${idx}`}
                className="mt-4 bg-base-200 rounded-lg p-4"
              >
                <MermaidRenderer code={diagram} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
